import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Scale, Layers, Gauge, RotateCcw, Activity, AudioWaveform, Feather } from 'lucide-react';
import { Seo } from '../components/Seo';
import { PADDLES, METRICS, TECH_FAQ, TECH_IMG, GLOSSARY, TEST_METHOD_NOTE } from '../data/technicals';
import { MarketGauges } from '../components/technicals/TechInteractive';
import { MetricBars } from '../components/technicals/MetricBars';
import { FlexCurve, SoundSignature, SandwichDiagram } from '../components/technicals/TechDiagrams';
import { PaddleCtas, PaddleSelector, TechFaq, DataReference } from '../components/technicals/TechBlocks';

const SITE = 'https://dyssc.com';
const metric = (k) => METRICS.find((m) => m.key === k);

const Eyebrow = ({ children, color }) => (
  <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.32em] font-bold" style={{ color: color || '#A1A1AA' }}>{children}</p>
);

const Section = ({ id, children, className = '' }) => (
  <section id={id} className={`relative py-14 md:py-20 scroll-mt-16 ${className}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
  </section>
);

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-10% 0px' },
  transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
});

const HL_ICONS = { hexcore: [RotateCcw, Gauge, Activity, AudioWaveform], profoam: [Feather, Gauge, Activity, AudioWaveform] };

export default function Technicals() {
  const jsonLd = [
    {
      '@context': 'https://schema.org', '@type': 'TechArticle',
      headline: 'HexCore vs ProFoam: measured pickleball paddle data — weight, swing weight, twist weight, bending and impact sound',
      description: 'Measured product data for the DYSSC HexCore (polypropylene honeycomb core) and ProFoam (foam core) carbon pickleball paddles, from one laboratory measurement programme.',
      image: [TECH_IMG.cutaway], url: `${SITE}/technicals`, inLanguage: 'en-IN',
      author: { '@type': 'Organization', name: 'DYSSC' }, publisher: { '@type': 'Organization', name: 'DYSSC', url: SITE },
      about: [
        { '@type': 'Product', name: 'DYSSC HexCore Pro', url: `${SITE}/product/hexcore` },
        { '@type': 'Product', name: 'DYSSC ProFoam Pro', url: `${SITE}/product/profoam` },
      ],
      keywords: 'pickleball paddle swing weight, pickleball paddle twist weight, honeycomb core vs foam core pickleball paddle, carbon fibre pickleball paddle India, 16mm pickleball paddle',
    },
    {
      '@context': 'https://schema.org', '@type': 'DefinedTermSet', name: 'Pickleball paddle measurement glossary',
      hasDefinedTerm: GLOSSARY.map((g) => ({ '@type': 'DefinedTerm', name: g.q, description: g.a })),
    },
    { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: TECH_FAQ.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
    { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
      { '@type': 'ListItem', position: 2, name: 'Technicals', item: `${SITE}/technicals` },
    ] },
  ];

  return (
    <div className="bg-[#0B0B0C] text-white" data-testid="technicals-page">
      <Seo
        title="Pickleball Paddle Technicals: HexCore vs ProFoam Data | DYSSC"
        description="Measured pickleball paddle data: weight, balance, swing weight, twist weight, bending and impact sound for DYSSC HexCore (honeycomb core) and ProFoam (foam core). Interactive charts, teardown and glossary."
        canonical={`${SITE}/technicals`}
        image={TECH_IMG.cutaway}
        jsonLd={jsonLd}
      />

      {/* ---------- HERO ---------- */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_20%_30%,rgba(224,33,40,0.16),transparent_60%),radial-gradient(ellipse_50%_50%_at_85%_70%,rgba(30,111,217,0.18),transparent_60%)]" />
        <div className="absolute inset-0 bg-texture opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 md:pt-16 pb-10 md:pb-14 grid lg:grid-cols-12 gap-8 lg:gap-6 items-center">
          <div className="lg:col-span-5">
            <motion.div {...fade(0)}><Eyebrow>Technicals · Product data</Eyebrow></motion.div>
            <motion.h1 {...fade(0.1)} className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] tracking-tight">
              Two core<br />constructions.<br />
              <span className="text-zinc-400">A clear choice for your game.</span>
            </motion.h1>
            <motion.p {...fade(0.2)} className="mt-5 text-zinc-300 text-base md:text-lg leading-relaxed max-w-md">
              HexCore feels firmer and steadier. ProFoam is lighter and quicker to swing. Same carbon faces, same shape — a different core inside.
            </motion.p>
            <motion.p {...fade(0.25)} className="mt-3 text-zinc-500 text-sm leading-relaxed max-w-md" data-testid="tech-method-note">
              {TEST_METHOD_NOTE}
            </motion.p>
            <motion.div {...fade(0.3)} className="mt-7 flex flex-wrap gap-3">
              <Link to="/compare" className="inline-flex items-center gap-2 rounded-full bg-white text-black font-bold uppercase tracking-[0.15em] text-xs sm:text-sm px-7 py-3.5 hover:scale-[1.03] active:scale-[0.97] transition-transform shadow-[0_12px_30px_rgba(255,255,255,0.15)]" data-testid="tech-hero-compare">
                <Scale className="w-4 h-4" /> Compare paddles
              </Link>
              <a href="#choose" className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-white/60 text-white font-bold uppercase tracking-[0.15em] text-xs sm:text-sm px-7 py-3.5 transition-colors" data-testid="tech-hero-explore">
                Explore the data <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>
            <motion.div {...fade(0.4)} className="mt-8 grid grid-cols-2 gap-4 max-w-md">
              {[PADDLES.hexcore, PADDLES.profoam].map((p) => (
                <div key={p.slug} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <p className="font-display text-xl uppercase tracking-tight" style={{ color: p.accent }}>{p.name}</p>
                  <p className="text-xs text-zinc-400 mt-1">{p.identity}</p>
                </div>
              ))}
            </motion.div>
          </div>
          <motion.div {...fade(0.15)} className="lg:col-span-7">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black shadow-[0_40px_80px_rgba(0,0,0,0.6)]" data-testid="core-cutaway">
              <div className="grid grid-cols-2 gap-2 sm:gap-6 px-4 sm:px-10 pt-4 sm:pt-6">
                {[{ p: PADDLES.hexcore, src: TECH_IMG.renderHex, core: 'PP honeycomb core' }, { p: PADDLES.profoam, src: TECH_IMG.renderPro, core: 'Foam core' }].map(({ p, src, core }) => (
                  <figure key={p.slug} className="text-center" data-testid={`cutaway-${p.slug}`}>
                    <img src={src} alt={`${p.fullName} with the carbon face cut away to show its ${core.toLowerCase()}`} width="509" height="1225" className="w-full h-auto" fetchPriority="high" />
                    <figcaption className="pb-4 sm:pb-5 -mt-2">
                      <p className="font-display text-xl sm:text-2xl uppercase tracking-tight" style={{ color: p.accent }}>{p.name}</p>
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mt-0.5">{core}</p>
                    </figcaption>
                  </figure>
                ))}
              </div>
              <div className="px-4 sm:px-10 py-3 border-t border-white/[0.08]">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Core visualisation · same carbon faces, same 16 mm build, different core</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ---------- CHOOSE YOUR CORE ---------- */}
      <Section id="choose">
        <motion.div {...fade(0)} className="max-w-2xl">
          <Eyebrow>01 · Choose your core</Eyebrow>
          <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl">Your core. Your preference.</h2>
          <p className="mt-3 text-zinc-400">Shared foundation: carbon faces, a 16 mm face-and-core sandwich and closely matching outer shapes. The core architecture creates a meaningful choice.</p>
        </motion.div>
        <div className="mt-10 grid md:grid-cols-2 gap-5 lg:gap-8">
          {[PADDLES.hexcore, PADDLES.profoam].map((p, i) => (
            <motion.div key={p.slug} {...fade(0.1 * i)} className="group relative rounded-2xl border border-white/10 bg-[#101013] overflow-hidden" data-testid={`core-card-${p.slug}`}>
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: `radial-gradient(ellipse 60% 50% at 50% 100%, ${p.accent}22, transparent 70%)` }} />
              <div className="relative grid grid-cols-1 sm:grid-cols-[1fr_160px] gap-5 p-6 sm:p-8">
                <div className="relative h-44 sm:hidden -mx-6 -mt-6 overflow-hidden">
                  <img src={p.slug === 'hexcore' ? TECH_IMG.renderHex : TECH_IMG.renderPro} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#101013] to-transparent" />
                </div>
                <div>
                  <Eyebrow color={p.accent}>{p.name}</Eyebrow>
                  <h3 className="mt-3 font-heading font-bold text-2xl sm:text-3xl leading-tight">{p.identity}</h3>
                  <p className="mt-3 text-sm sm:text-base text-zinc-400 leading-relaxed">{p.summary}</p>
                  <p className="mt-4 text-sm text-zinc-200"><span className="font-semibold text-white">Choose {p.name} for:</span> {p.chooseFor}</p>
                  <div className="mt-6 grid grid-cols-2 gap-3">
                    {p.stats.map((s) => (
                      <div key={s.label} className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
                        <p className="font-mono text-2xl sm:text-3xl" style={{ color: p.accent }}>{s.value}</p>
                        <p className="text-[11px] text-zinc-300 mt-1 leading-tight">{s.label}</p>
                        <p className="text-[10px] text-zinc-500">{s.note}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="relative hidden sm:block">
                  <img src={p.slug === 'hexcore' ? TECH_IMG.renderHex : TECH_IMG.renderPro} alt={`${p.fullName} core cutaway`} className="absolute inset-0 w-full h-full object-cover rounded-xl border border-white/10 transition-transform duration-700 group-hover:scale-[1.04]" />
                </div>
              </div>
              <div className="relative px-6 sm:px-8 pb-6 sm:pb-8">
                <Link to={`/product/${p.slug}`} className="inline-flex items-center gap-2 rounded-full text-white font-bold uppercase tracking-[0.15em] text-xs px-6 py-3 hover:scale-[1.03] active:scale-[0.97] transition-transform" style={{ backgroundColor: p.accent }} data-testid={`core-card-cta-${p.slug}`}>
                  Explore {p.name} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ---------- HIGHLIGHTS ---------- */}
      <Section id="highlights" className="border-t border-white/[0.06]">
        <motion.div {...fade(0)} className="max-w-2xl">
          <Eyebrow>02 · Product highlights</Eyebrow>
          <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl">Four reasons for each shortlist</h2>
        </motion.div>
        <div className="mt-10 grid md:grid-cols-2 gap-8 lg:gap-12">
          {[PADDLES.hexcore, PADDLES.profoam].map((p) => (
            <div key={p.slug} data-testid={`highlights-${p.slug}`}>
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px flex-1" style={{ background: `linear-gradient(90deg, ${p.accent}, transparent)` }} />
                <span className="font-display text-2xl uppercase tracking-tight" style={{ color: p.accent }}>{p.name}</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
                {p.highlights.map((h, i) => {
                  const Icon = HL_ICONS[p.slug][i];
                  return (
                    <motion.div key={h.title} {...fade(0.06 * i)} className="bg-[#0F0F11] p-5 hover:bg-[#141417] transition-colors">
                      <Icon className="w-5 h-5" style={{ color: p.accent }} strokeWidth={1.8} />
                      <p className="mt-3 font-heading font-semibold text-white">{h.title}</p>
                      <p className="mt-1.5 text-xs text-zinc-500 font-mono">{h.data}</p>
                      <p className="mt-2 text-sm text-zinc-300 leading-snug">“{h.copy}”</p>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ---------- DATA ---------- */}
      <Section id="data" className="border-t border-white/[0.06]">
        <motion.div {...fade(0)} className="max-w-2xl">
          <Eyebrow>03 · Side-by-side data</Eyebrow>
          <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl">The numbers behind the preference</h2>
          <p className="mt-3 text-zinc-400">Neither value is universally better. Choose the build you prefer.</p>
        </motion.div>

        <div className="mt-10 grid lg:grid-cols-2 gap-5 lg:gap-6">
          {[
            { m: metric('weight'), title: 'Solid or light', sub: 'HexCore has a bit more weight in the hand. ProFoam is the lighter option — 13 g less.' },
            { m: metric('twist'), title: 'Steady on off-centre hits', sub: 'HexCore has the higher twist weight (5.67 vs 5.49), so it twists a little less when the ball lands off-centre.' },
            { m: metric('bending'), title: 'Firmer on touch shots', sub: 'HexCore\'s face bends about 37% less on soft shots. On hard shots ProFoam firms up and the two feel closer.' },
            { m: metric('swing'), title: 'Quicker to swing', sub: 'ProFoam\'s swing weight is 6.4% lower than HexCore — easier to get around at the kitchen line.' },
          ].map(({ m, title, sub }, i) => (
            <motion.div key={m.key} {...fade(0.08 * i)} className="rounded-2xl border border-white/10 bg-[#0F0F11] p-6 sm:p-8" data-testid={`data-card-${m.key}`}>
              <h3 className="font-heading font-bold text-xl sm:text-2xl">{title}</h3>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{sub}</p>
              <div className="mt-6"><MetricBars metric={m} /></div>
            </motion.div>
          ))}
        </div>

        <div className="mt-6 grid lg:grid-cols-2 gap-5 lg:gap-6">
          <motion.div {...fade(0)} className="rounded-2xl border border-white/10 bg-[#0F0F11] p-6 sm:p-8" data-testid="data-card-flex">
            <Eyebrow>Flex character</Eyebrow>
            <h3 className="mt-2 font-heading font-bold text-xl sm:text-2xl">Linear vs progressive</h3>
            <p className="mt-2 text-sm text-zinc-400 leading-relaxed">HexCore bends the same way on every shot. ProFoam gives more on soft shots and firms up on hard ones.</p>
            <div className="mt-6"><FlexCurve /></div>
          </motion.div>
          <motion.div {...fade(0.1)} className="rounded-2xl border border-white/10 bg-[#0F0F11] p-6 sm:p-8" data-testid="data-card-sound">
            <Eyebrow>Impact signature</Eyebrow>
            <h3 className="mt-2 font-heading font-bold text-xl sm:text-2xl">Two distinct sound characters</h3>
            <p className="mt-2 text-sm text-zinc-400 leading-relaxed">HexCore has a deeper-toned pop (785 Hz); ProFoam is brighter (908 Hz). Measured by dropping a ball onto the face. This is tone, not loudness.</p>
            <div className="mt-6"><SoundSignature /></div>
          </motion.div>
        </div>
      </Section>

      {/* ---------- MARKET CONTEXT ---------- */}
      <Section id="context" className="border-t border-white/[0.06]">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          <motion.div {...fade(0)} className="lg:col-span-4">
            <Eyebrow>04 · In context</Eyebrow>
            <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl">Where DYSSC sits among published paddles</h2>
            <p className="mt-3 text-zinc-400">The grey band shows where other quality paddles sit. Both DYSSC paddles land right in that company — one solid and steady, one light and quick.</p>
          </motion.div>
          <motion.div {...fade(0.1)} className="lg:col-span-8 rounded-2xl border border-white/10 bg-[#0F0F11] p-6 sm:p-8"><MarketGauges /></motion.div>
        </div>
      </Section>

      {/* ---------- CONSTRUCTION ---------- */}
      <Section id="construction" className="border-t border-white/[0.06]">
        <motion.div {...fade(0)} className="max-w-2xl">
          <Eyebrow>05 · Construction</Eyebrow>
          <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl">Carbon faces with different internal architecture</h2>
          <p className="mt-3 text-zinc-400">Both paddles share a 16 mm face-and-core sandwich in a closely matching shape family. The core creates the choice.</p>
        </motion.div>
        <div className="mt-10 grid md:grid-cols-2 gap-5 lg:gap-8">
          {[PADDLES.hexcore, PADDLES.profoam].map((p, i) => (
            <motion.div key={p.slug} {...fade(0.1 * i)} className="rounded-2xl border border-white/10 bg-[#0F0F11] overflow-hidden" data-testid={`construction-${p.slug}`}>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_150px]">
                <div className="relative h-40 sm:h-auto sm:order-2">
                  <img src={p.slug === 'hexcore' ? TECH_IMG.coreHex : TECH_IMG.corePro} alt={`${p.name} core section`} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t sm:bg-gradient-to-r from-[#0F0F11] to-transparent" />
                  <span className="absolute bottom-3 right-3 font-mono text-[9px] uppercase tracking-[0.2em] text-white/80 bg-black/50 px-2 py-1 rounded">Core section</span>
                </div>
                <div className="p-6 sm:p-8 sm:order-1">
                  <Eyebrow color={p.accent}>{p.name}</Eyebrow>
                  <h3 className="mt-2 font-heading font-bold text-xl sm:text-2xl">{p.slug === 'hexcore' ? 'Polypropylene honeycomb' : 'Foam core with internal channels'}</h3>
                  <div className="mt-5"><SandwichDiagram variant={p.slug} /></div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ---------- SELECTOR ---------- */}
      <Section id="selector" className="border-t border-white/[0.06]">
        <motion.div {...fade(0)} className="max-w-2xl mb-10">
          <Eyebrow>06 · Preference selector</Eyebrow>
          <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl">Which DYSSC build suits your preference?</h2>
        </motion.div>
        <PaddleSelector />
      </Section>

      {/* ---------- CTA + FAQ ---------- */}
      <Section id="faq" className="border-t border-white/[0.06]">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Eyebrow>07 · Find your paddle</Eyebrow>
            <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl">Compare the data and choose your DYSSC.</h2>
            <p className="mt-3 text-zinc-400">Prefer a firmer feel and a bit more weight in the hand? Explore HexCore. Prefer a lighter, quicker paddle? Explore ProFoam.</p>
            <PaddleCtas className="mt-7" />
            <div className="mt-10"><DataReference /></div>
          </div>
          <div className="lg:col-span-7">
            <Eyebrow>Frequently asked</Eyebrow>
            <div className="mt-3"><TechFaq /></div>
            <div className="mt-12" id="glossary">
              <Eyebrow>Glossary · what the numbers mean</Eyebrow>
              <div className="mt-3" data-testid="tech-glossary"><TechFaq items={GLOSSARY} /></div>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
